import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable, HttpsCallableResult } from '@angular/fire/functions';

/**
 * Firebase Functions Service
 *
 * Provides type-safe wrapper for calling cloud functions
 * Handles errors and provides consistent interface
 */
@Injectable({
  providedIn: 'root'
})
export class FunctionsService {
  private functions = inject(Functions);

  /**
   * Call a cloud function by name
   *
   * @param functionName Name of the cloud function to call
   * @param data Data to pass to the function
   * @param options Optional configuration (timeout, etc.)
   * @returns Promise with function result data
   *
   * @example
   * const result = await functionsService.call<LicenseData>('get_license', { userId: '123' });
   */
  async call<T = any>(
    functionName: string,
    data: any = {},
    options: {
      timeout?: number;
      retries?: number;
    } = {}
  ): Promise<T> {
    const { timeout = 30000, retries = 1 } = options;

    console.log(`🔗 [FunctionsService] Calling function: ${functionName}`, { data });

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const callable = httpsCallable<any, T>(this.functions, functionName);
        const result = await callable(data);
        console.log(`🔗 [FunctionsService] Function ${functionName} result:`, result.data);
        return result.data;
      } catch (error: any) {
        console.error(`❌ [FunctionsService] Function ${functionName} error:`, error);
        lastError = this.handleFunctionError(error, functionName);

        // Don't retry on auth errors or validation errors
        if (this.isNonRetryableError(error)) {
          break;
        }

        // Wait before retry (exponential backoff)
        if (attempt < retries) {
          await this.delay(Math.pow(2, attempt) * 1000);
        }
      }
    }

    throw lastError || new Error(`Failed to call function ${functionName}`);
  }

  /**
   * Handle cloud function errors
   */
  private handleFunctionError(error: any, functionName: string): Error {
    let message = `Error calling ${functionName}`;

    if (error.code) {
      switch (error.code) {
        case 'unauthenticated':
          message = 'You must be logged in to perform this action';
          break;
        case 'permission-denied':
          message = 'You do not have permission to perform this action';
          break;
        case 'not-found':
          message = 'The requested resource was not found';
          break;
        case 'already-exists':
          message = 'The resource already exists';
          break;
        case 'invalid-argument':
          message = 'Invalid request parameters';
          break;
        case 'deadline-exceeded':
          message = 'Request timed out. Please try again';
          break;
        case 'unavailable':
          message = 'Service temporarily unavailable. Please try again';
          break;
        default:
          message = error.message || message;
      }
    } else {
      message = error.message || message;
    }

    const newError = new Error(message);
    (newError as any).code = error.code;
    (newError as any).details = error.details;

    return newError;
  }

  /**
   * Check if error should not be retried
   */
  private isNonRetryableError(error: any): boolean {
    const nonRetryableCodes = [
      'unauthenticated',
      'permission-denied',
      'invalid-argument',
      'not-found',
      'already-exists'
    ];
    return nonRetryableCodes.includes(error.code);
  }

  /**
   * Delay helper for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
